/**
 * Shows which conversation context the shell is currently bound to.
 *
 * The shell is universal, so the user must always be able to see whether they are
 * talking to a specialist agent run or to standard T-MOS operations (REQ-AIS-038).
 * Presentational only: context switching is not part of the foundation.
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { ActiveConversationContext, AiStudioContextType } from '@/types/aiStudio';

const CONTEXT_LABEL_KEYS: Record<AiStudioContextType, string> = {
  AGENT_RUN: 'aiStudio.conversation.contextAgentRun',
  TMOS_OPERATIONS: 'aiStudio.conversation.contextTmosOperations',
  AGENT_AUTHORING: 'aiStudio.conversation.contextAgentAuthoring',
  INSTANCE_SETUP: 'aiStudio.conversation.contextInstanceSetup',
  PACK_AUTHORING: 'aiStudio.conversation.contextPackAuthoring',
  INSTANCE_DEPLOY: 'aiStudio.conversation.contextInstanceDeploy',
  INSTANCE_PROVISION: 'aiStudio.conversation.contextInstanceProvision',
};

interface ActiveContextIndicatorProps {
  context: ActiveConversationContext;
  className?: string;
}

export const ActiveContextIndicator: React.FC<ActiveContextIndicatorProps> = ({
  context,
  className = '',
}) => {
  const { t } = useLanguage();
  const contextLabel = t(CONTEXT_LABEL_KEYS[context.contextType]);

  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary-bg)] px-3 py-2 ${className}`}
      data-context-type={context.contextType}
    >
      <span className="text-sm font-medium uppercase tracking-wide text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.conversation.activeContext')}
      </span>
      <span className="rounded-md bg-[var(--color-accent-bg)] px-2 py-0.5 text-sm font-medium text-[var(--color-accent-text)]">
        {contextLabel}
      </span>
      {context.scopeLabel && (
        <span className="min-w-0 truncate text-base text-[var(--color-surface-text)]" title={context.scopeLabel}>
          {context.scopeLabel}
        </span>
      )}
      {context.status && context.status !== 'open' && (
        <span className="text-sm text-[var(--color-surface-text-secondary)]">
          {t(`aiStudio.conversation.status.${context.status}`)}
        </span>
      )}
    </div>
  );
};
